import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/data";

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-24%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-bone lg:min-h-[100svh]"
    >
      {/* Desktop background photo */}
      <motion.img
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ scale: imgScale }}
        src={IMAGES.hero}
        alt="Mature women walking together along the shore at sunset"
        className="hidden lg:block absolute inset-0 h-full w-full object-cover object-[72%_center]"
      />
      {/* Desktop veils */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[var(--astera-bone)] from-15% via-[var(--astera-bone)]/85 via-40% to-transparent to-70%" />
      <div className="hidden lg:block absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[var(--astera-bone)]/70 to-transparent" />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:min-h-[100svh]">
        {/* Text */}
        <motion.div style={{ y: textY }} className="pt-28 pb-6 lg:pb-16 lg:max-w-[620px]">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mb-6 lg:mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[var(--astera-char)]/70"
          >
            <span className="h-[1px] w-10 bg-clay" />
            Physician-Led · Menopause · Hormones
          </motion.p>

          <h1 className="font-serif font-light leading-[0.94] tracking-tight text-[var(--astera-char)] text-[15vw] sm:text-[11vw] lg:text-[5.4vw]">
            <MaskLines lines={["Care for every"]} delay={0.5} />
            <MaskLines lines={["season of"]} delay={0.65} className="italic text-clay" />
            <MaskLines lines={["womanhood."]} delay={0.8} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-6 lg:mt-8 text-[var(--astera-char)]/80 leading-relaxed text-sm sm:text-base max-w-md"
          >
            Expert, unhurried care for perimenopause, menopause, hormone health
            and intimate wellbeing — led personally by a double board-certified
            physician, never a substitute.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 1 }}
            className="mt-5 lg:mt-6 font-serif italic text-xl sm:text-2xl text-clay"
          >
            Take your health personally. We do.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-8 lg:mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onBook}
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 rounded-full bg-clay px-7 sm:px-8 py-4 text-sm uppercase tracking-[0.16em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-[var(--astera-char)]"
            >
              Book an Appointment
              <ArrowDownRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </button>
            <a
              href="#services"
              data-testid="hero-explore-link"
              className="link-underline text-sm uppercase tracking-[0.16em] text-[var(--astera-char)]/80"
            >
              Explore our care
            </a>
          </motion.div>
        </motion.div>

        {/* Mobile / tablet contained image */}
        <div className="lg:hidden pb-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="aspect-[3/2] w-full overflow-hidden rounded-[24px]"
          >
            <img
              src={IMAGES.hero}
              alt="Mature women walking together along the shore at sunset"
              className="h-full w-full object-cover object-center"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
